import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PenLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function SignAgreement() {
  const [data, setData] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/portal/overview").then((r) => {
      setData(r.data);
      setAgreement(r.data.agreement);
    }).catch(() => {});
  }, []);

  if (!data) return <div className="py-20 text-center text-slate-400 text-sm">Loading agreement…</div>;

  const signed = data.client.agreement_signed && agreement?.status === "signed";
  const text = (agreement?.agreement_text || "").replace("{company}", data.company) ||
    `Service agreement with ${data.company}. Start signing to load the full agreement text.`;

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/portal/esign/start");
      setAgreement(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const complete = async () => {
    if (!agreement) return toast.error("Load the agreement first");
    if (!name.trim()) return toast.error("Type your full legal name");
    if (!consent) return toast.error("Please check the consent box");
    setBusy(true);
    try {
      const r = await api.post(`/portal/esign/${agreement.id}/complete`, { signed_name: name, consent });
      setAgreement(r.data);
      toast.success("Agreement signed — thank you");
      const o = await api.get("/portal/overview");
      setData(o.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="portal-sign-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Service agreement</h1>
        <p className="text-slate-600 mt-1">Review and sign electronically. This authorizes us to dispute inaccurate items on your behalf.</p>
      </div>

      {signed ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center space-y-2" data-testid="agreement-signed-card">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
          <h2 className="font-heading font-semibold text-lg text-emerald-900">Agreement signed</h2>
          <p className="text-sm text-emerald-800">Signed by <b>{agreement.signed_name}</b> on {fmtDate(agreement.signed_at)}.</p>
          <p className="text-xs text-emerald-700">A copy is stored in your records. Remember: you may cancel within 3 business days under the Credit Repair Organizations Act.</p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <span className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500">Agreement text</span>
              {!agreement && (
                <Button size="sm" variant="outline" onClick={start} disabled={busy} data-testid="load-agreement-btn">
                  {busy ? "Preparing…" : "Load agreement"}
                </Button>
              )}
            </div>
            <pre className="p-5 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto" data-testid="agreement-text">
              {agreement ? agreement.agreement_text?.replace("{company}", data.company) : "Click “Load agreement” to generate your personalized service agreement."}
            </pre>
          </div>

          {agreement && agreement.status !== "signed" && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
              <div className="flex items-center gap-2">
                <PenLine className="h-4 w-4 text-slate-500" />
                <h3 className="font-heading font-semibold text-slate-800">Sign electronically</h3>
              </div>
              <div>
                <Label>Type your full legal name (this is your signature)</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full legal name" className="mt-1 font-heading text-lg italic" data-testid="esign-name-input" />
              </div>
              <label className="flex items-start gap-2.5 text-sm text-slate-600">
                <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="esign-consent-checkbox" />
                <span>I agree to the terms above, I consent to sign electronically, and I understand I can cancel within 3 business days under the Credit Repair Organizations Act.</span>
              </label>
              <Button onClick={complete} disabled={busy} data-testid="client-esign-submit-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white font-semibold">
                {busy ? "Signing…" : "Sign agreement"}
              </Button>
            </div>
          )}
        </>
      )}
      <div className="hidden">{text.length}</div>
    </div>
  );
}
